import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'

import { Maybe, MembershipSubscriptionPlan } from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'

import { CreateItemProps } from '../ItemCreateForm'
import { ToggleInputCell } from './Commons'

type PremiumInputCellProps = {
  register: UseFormRegister<CreateItemProps>
  setValue: UseFormSetValue<CreateItemProps>
  watch: UseFormWatch<CreateItemProps>
  plans: Maybe<Pick<MembershipSubscriptionPlan, 'title' | 'planId'>>[]
  defaultValue: string[]
  showCheckboxError: boolean
}

export const PremiumInputCell = ({
  register,
  setValue,
  watch,
  plans,
  defaultValue,
  showCheckboxError,
}: PremiumInputCellProps) => {
  const { t } = useTranslation('consoleItemCreate')
  const isPremium = watch('isPremium')
  const { onChange: registerOnChange, ...registerProps } = register('isPremium')

  return (
    <>
      <ToggleInputCell
        content={{
          title: t('membersOnly'),
          description: (
            <Stack>
              <Typography variant="body2" color="text.secondary">
                {t('membersOnlyDesc')}
              </Typography>
            </Stack>
          ),
        }}
        checked={isPremium}
        onChange={() => {
          if (isPremium) {
            setValue('purchesablePlanIds', [], {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('isPremium', false, {
              shouldValidate: true,
              shouldDirty: true,
            })
          } else {
            setValue('purchesablePlanIds', defaultValue, {
              shouldValidate: true,
              shouldDirty: true,
            })
            setValue('isPremium', true, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        }}
        {...registerProps}
      />
      {isPremium && plans.length > 1 && (
        <FormControl error={showCheckboxError}>
          {plans.map((plan, index) => (
            <Box key={index}>
              <FormControlLabel
                key={plan?.planId}
                label={plan?.title}
                {...register('purchesablePlanIds')}
                control={
                  <Checkbox
                    onChange={e => {
                      const currentPlanIds = watch('purchesablePlanIds') || []
                      if (e.target.checked) {
                        // チェックされた場合、planIdを追加
                        const newPlanIds = [
                          ...currentPlanIds,
                          plan?.planId ?? '', // planIdがundefinedの場合は空文字を使用
                        ]
                        setValue('purchesablePlanIds', newPlanIds, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      } else {
                        // チェックが解除された場合、planIdを削除
                        const newPlanIds = currentPlanIds.filter(
                          id => id !== plan?.planId
                        )
                        setValue('purchesablePlanIds', newPlanIds, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                    }}
                    checked={
                      watch('purchesablePlanIds')?.includes(
                        plan?.planId ?? ''
                      ) || false
                    }
                  />
                }
              />
            </Box>
          ))}
          <Typography variant="caption" color="error.main">
            {showCheckboxError ? t('membershipAccessPermissionRequired') : ' '}
          </Typography>
        </FormControl>
      )}
    </>
  )
}
